/**
 * Lightweight replacement for the Swiper 11 bundle.
 *
 * Implements only the subset of the Swiper API used in this theme (swiper-*.js) and in the
 * tankbar-multiblock plugin (view.js), with the same DOM class names and the same layout math
 * as Swiper 11 (updateSlides/slideTo/onTouchEnd), so markup and CSS stay untouched.
 *
 * Supported params: slidesPerView ('auto' | number), spaceBetween, slidesOffsetBefore/After, speed,
 * loop, effect ('slide' | 'fade'), allowTouchMove, grabCursor, breakpoints (min-width, window based),
 * autoplay { delay }, navigation { nextEl, prevEl }, pagination { el, type: bullets|fraction|progressbar }.
 */
(function () {
    'use strict';

    const THRESHOLD = 5; // px before a pointer move counts as a drag (Swiper default)
    const TOUCH_ANGLE = 45;
    const RESISTANCE_RATIO = 0.85;
    const LONG_SWIPES_MS = 300;
    const LONG_SWIPES_RATIO = 0.5;

    const DEFAULTS = {
        slidesPerView: 1,
        spaceBetween: 0,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
        speed: 300,
        loop: false,
        effect: 'slide',
        allowTouchMove: true,
        grabCursor: false,
        autoplay: false,
        navigation: false,
        pagination: false,
        breakpoints: null,
    };

    const resolveEl = (el, scope) => {
        if (!el) return null;
        if (typeof el !== 'string') return el;
        const matches = document.querySelectorAll(el);
        if (matches.length > 1 && scope && scope.querySelectorAll(el).length === 1) return scope.querySelector(el);
        return matches[0] || null;
    };

    class Swiper {
        constructor(el, params = {}) {
            this.el = resolveEl(el);
            if (!this.el || this.el.swiper) return;
            this.wrapperEl = this.el.querySelector('.swiper-wrapper');
            if (!this.wrapperEl) return;

            this.el.swiper = this;
            this.userParams = params;
            this.params = this.getParams();
            this.translate = 0;
            this.snapIndex = 0;
            this.activeIndex = 0;
            this.realIndex = 0;
            this.allowClick = true;

            this.slides = this.getSlides();
            this.slides.forEach((slide, i) => slide.setAttribute('data-swiper-slide-index', i));

            this.el.classList.add('swiper-initialized', 'swiper-horizontal');
            // Swiper only enables backface-visibility: hidden up to maxBackfaceHiddenSlides (10)
            this.el.classList.toggle('swiper-backface-hidden', this.slides.length <= 10);
            if (this.params.effect === 'fade') this.el.classList.add('swiper-fade');

            this.initNavigation();
            this.initPagination();
            this.initTouch();
            this.update();
            this.initAutoplay();

            this.resizeObserver = new ResizeObserver(() => {
                cancelAnimationFrame(this.resizeFrame);
                this.resizeFrame = requestAnimationFrame(() => this.onResize());
            });
            this.resizeObserver.observe(this.el);
        }

        getParams() {
            const params = Object.assign({}, DEFAULTS, this.userParams);
            const breakpoints = this.userParams.breakpoints;
            if (breakpoints) {
                Object.keys(breakpoints)
                    .map(Number)
                    .sort((a, b) => a - b)
                    .forEach((bp) => {
                        if (window.innerWidth >= bp) Object.assign(params, breakpoints[bp]);
                    });
            }
            return params;
        }

        getSlides() {
            return Array.from(this.wrapperEl.children).filter((child) => child.classList.contains('swiper-slide'));
        }

        onResize() {
            this.params = this.getParams();
            this.update();
        }

        /* ---------- Layout (mirrors Swiper 11 updateSlides) ---------- */

        update() {
            const params = this.params;
            const size = this.el.clientWidth;
            const space = params.spaceBetween;
            const slides = this.slides;
            this.size = size;

            this.slides.forEach((slide) => { slide.style.marginRight = ''; });

            const snapGrid = [];
            const slidesGrid = [];
            const slidesSizesGrid = [];
            let position = -params.slidesOffsetBefore;
            let virtualSize = -space;

            slides.forEach((slide) => {
                let slideSize;
                if (params.slidesPerView === 'auto') {
                    const cs = getComputedStyle(slide);
                    const transform = slide.style.transform;
                    if (transform) slide.style.transform = 'none';
                    slideSize = parseFloat(cs.width) + parseFloat(cs.marginLeft) + parseFloat(cs.marginRight);
                    if (cs.boxSizing !== 'border-box') {
                        slideSize += parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + (slide.offsetWidth - slide.clientWidth);
                    }
                    if (transform) slide.style.transform = transform;
                } else {
                    slideSize = (size - (params.slidesPerView - 1) * space) / params.slidesPerView;
                    slide.style.width = `${slideSize}px`;
                }
                slidesSizesGrid.push(slideSize);
                snapGrid.push(position);
                slidesGrid.push(position);
                position += slideSize + space;
                virtualSize += slideSize + space;
            });

            virtualSize = Math.max(virtualSize, size) + params.slidesOffsetAfter;
            const filtered = snapGrid.filter((snap) => snap <= virtualSize - size);
            if (!filtered.length || Math.floor(virtualSize - size) - Math.floor(filtered[filtered.length - 1]) > 1) {
                filtered.push(virtualSize - size);
            }
            this.snapGrid = filtered.length ? filtered : [0];
            this.slidesGrid = slidesGrid;
            this.slidesSizesGrid = slidesSizesGrid;
            this.virtualSize = virtualSize;

            if (space !== 0) slides.forEach((slide) => { slide.style.marginRight = `${space}px`; });

            this.checkOverflow();

            if (params.effect === 'fade') {
                slides.forEach((slide, i) => {
                    slide.style.transform = `translate3d(${-slidesGrid[i] - params.slidesOffsetBefore}px, 0px, 0px)`;
                });
                this.setFade(0);
            } else {
                // Same as Swiper on resize: stay on the active slide
                this.snapIndex = Math.min(this.activeIndex, this.snapGrid.length - 1);
                this.setTransition(0);
                this.setTranslate(-this.snapGrid[this.snapIndex]);
                this.updateActiveIndex();
            }
            this.updateState();
        }

        checkOverflow() {
            const { slidesOffsetBefore } = this.params;
            if (this.slides.length <= 1) {
                this.isLocked = true;
            } else if (slidesOffsetBefore) {
                const last = this.slidesGrid.length - 1;
                const lastSlideRightEdge = this.slidesGrid[last] + this.slidesSizesGrid[last] + slidesOffsetBefore * 2;
                this.isLocked = this.size > lastSlideRightEdge;
            } else {
                this.isLocked = this.snapGrid.length === 1;
            }
            this.wrapperEl.style.cursor = this.params.grabCursor && this.params.allowTouchMove && !this.isLocked ? 'grab' : '';
        }

        minTranslate() {
            return -this.snapGrid[0];
        }

        maxTranslate() {
            return -this.snapGrid[this.snapGrid.length - 1];
        }

        setTransition(duration) {
            this.wrapperEl.style.transitionDuration = `${duration}ms`;
        }

        setTranslate(translate) {
            this.translate = translate;
            this.wrapperEl.style.transform = `translate3d(${translate}px, 0px, 0px)`;
        }

        setFade(duration) {
            this.slides.forEach((slide, i) => {
                slide.style.opacity = i === this.activeIndex ? 1 : 0;
                slide.style.transitionDuration = `${duration}ms`;
            });
        }

        /* ---------- State (progress, active index, navigation, pagination) ---------- */

        updateActiveIndex() {
            const translate = -this.translate;
            const grid = this.slidesGrid;
            let index = this.activeIndex;
            const t = Math.floor(translate * 100);
            for (let i = 0; i < grid.length; i += 1) {
                const g = Math.floor(grid[i] * 100);
                if (typeof grid[i + 1] !== 'undefined') {
                    const gNext = Math.floor(grid[i + 1] * 100);
                    if (t >= g && t < gNext - (gNext - g) / 2) index = i;
                    else if (t >= g && t < gNext) index = i + 1;
                } else if (t >= g) {
                    index = i;
                }
            }
            this.activeIndex = Math.max(0, index);
        }

        updateState() {
            if (this.params.effect === 'fade') {
                this.isBeginning = this.activeIndex === 0;
                this.isEnd = this.activeIndex === this.slides.length - 1;
            } else {
                const min = this.minTranslate();
                const max = this.maxTranslate();
                const progress = max === min ? 0 : (this.translate - min) / (max - min);
                this.progress = progress;
                this.isBeginning = max === min || progress <= 0;
                this.isEnd = max === min || progress >= 1;
            }
            const active = this.slides[this.activeIndex];
            this.realIndex = active ? parseInt(active.getAttribute('data-swiper-slide-index'), 10) || 0 : 0;
            this.slides.forEach((slide, i) => {
                slide.classList.toggle('swiper-slide-active', i === this.activeIndex);
                slide.classList.toggle('swiper-slide-next', i === this.activeIndex + 1);
                slide.classList.toggle('swiper-slide-prev', i === this.activeIndex - 1);
            });
            this.updateNavigation();
            this.updatePagination();
        }

        initNavigation() {
            const nav = this.params.navigation;
            this.nextEl = nav ? resolveEl(nav.nextEl, this.el) : null;
            this.prevEl = nav ? resolveEl(nav.prevEl, this.el) : null;
            const bind = (button, label, action) => {
                if (!button) return;
                button.setAttribute('role', 'button');
                button.setAttribute('tabindex', '0');
                button.setAttribute('aria-label', label);
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (button.classList.contains('swiper-button-disabled')) return;
                    action();
                });
                button.addEventListener('keydown', (e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    button.click();
                });
            };
            bind(this.nextEl, 'Next slide', () => this.slideNext());
            bind(this.prevEl, 'Previous slide', () => this.slidePrev());
        }

        updateNavigation() {
            [[this.prevEl, this.isBeginning], [this.nextEl, this.isEnd]].forEach(([button, atEdge]) => {
                if (!button) return;
                const disabled = !this.params.loop && atEdge;
                button.classList.toggle('swiper-button-disabled', disabled);
                button.classList.toggle('swiper-button-lock', !!this.isLocked);
                button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
                button.setAttribute('tabindex', disabled ? '-1' : '0');
            });
        }

        initPagination() {
            const pagination = this.params.pagination;
            this.paginationEl = pagination ? resolveEl(pagination.el, this.el) : null;
            if (!this.paginationEl) return;
            this.paginationType = pagination.type || 'bullets';
            this.paginationEl.classList.add(`swiper-pagination-${this.paginationType}`, 'swiper-pagination-horizontal');
            if (this.paginationType === 'progressbar') {
                this.paginationEl.innerHTML = '<span class="swiper-pagination-progressbar-fill"></span>';
            } else if (this.paginationType === 'fraction') {
                this.paginationEl.innerHTML = '<span class="swiper-pagination-current"></span> / <span class="swiper-pagination-total"></span>';
            }
        }

        updatePagination() {
            const el = this.paginationEl;
            if (!el) return;
            const total = this.params.loop ? this.slides.length : this.snapGrid.length;
            const current = this.params.loop ? this.realIndex : (this.params.effect === 'fade' ? this.activeIndex : this.snapIndex);
            el.classList.toggle('swiper-pagination-lock', !!this.isLocked);

            if (this.paginationType === 'progressbar') {
                const fill = el.querySelector('.swiper-pagination-progressbar-fill');
                fill.style.transform = `translate3d(0,0,0) scaleX(${(current + 1) / total}) scaleY(1)`;
                fill.style.transitionDuration = `${this.params.speed}ms`;
            } else if (this.paginationType === 'fraction') {
                el.querySelector('.swiper-pagination-current').textContent = current + 1;
                el.querySelector('.swiper-pagination-total').textContent = total;
            } else {
                if (el.children.length !== total) {
                    el.innerHTML = '<span class="swiper-pagination-bullet"></span>'.repeat(total);
                }
                Array.from(el.children).forEach((bullet, i) => bullet.classList.toggle('swiper-pagination-bullet-active', i === current));
            }
        }

        /* ---------- Navigation between slides ---------- */

        slideTo(index, speed = this.params.speed) {
            if (this.params.effect === 'fade') {
                this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
                this.setFade(speed);
                this.updateState();
                this.onTransitionStart(speed);
                return;
            }
            const snapIndex = Math.max(0, Math.min(index, this.snapGrid.length - 1));
            this.snapIndex = snapIndex;
            this.setTransition(speed);
            this.setTranslate(-this.snapGrid[snapIndex]);
            this.updateActiveIndex();
            this.updateState();
            this.onTransitionStart(speed);
        }

        slideToLoop(realIndex, speed = this.params.speed) {
            const index = this.slides.findIndex((slide) => parseInt(slide.getAttribute('data-swiper-slide-index'), 10) === realIndex);
            this.slideTo(index < 0 ? 0 : index, speed);
        }

        slideNext(speed) {
            if (this.params.loop) this.loopFix('next');
            this.slideTo(this.activeIndex + 1, speed);
        }

        slidePrev(speed) {
            if (this.params.loop) {
                this.loopFix('prev');
                this.slideTo(this.activeIndex - 1, speed);
                return;
            }
            if (this.params.effect === 'fade') {
                this.slideTo(this.activeIndex - 1, speed);
                return;
            }
            const prevSnap = this.snapGrid[this.snapIndex - 1];
            let prevIndex = 0;
            if (typeof prevSnap !== 'undefined') {
                prevIndex = this.slidesGrid.indexOf(prevSnap);
                if (prevIndex < 0) prevIndex = this.activeIndex - 1;
            }
            this.slideTo(prevIndex, speed);
        }

        /**
         * Loop mode (slidesPerView 1): before moving past an edge, move the slide from the opposite end
         * next to the active one and compensate the translate without transition — like Swiper's loopFix.
         */
        loopFix(direction) {
            const count = this.slides.length;
            if (count < 2) return;
            if (direction === 'next' && this.activeIndex === count - 1) {
                this.wrapperEl.appendChild(this.slides[0]);
                this.activeIndex -= 1;
            } else if (direction === 'prev' && this.activeIndex === 0) {
                this.wrapperEl.prepend(this.slides[count - 1]);
                this.activeIndex += 1;
            } else {
                return;
            }
            this.slides = this.getSlides();
            if (this.params.effect === 'fade') {
                this.update();
                return;
            }
            // All loop slides share one width, so moving one slide shifts the rest by exactly one step
            const step = this.slidesSizesGrid[0] + this.params.spaceBetween;
            this.setTransition(0);
            this.setTranslate(this.translate + (direction === 'next' ? step : -step));
            this.snapIndex = this.activeIndex;
            this.wrapperEl.offsetWidth; // flush so the next transition starts from the compensated position
        }

        onTransitionStart(speed) {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = setTimeout(() => this.onTransitionEnd(), speed);
        }

        onTransitionEnd() {
            this.runAutoplay();
        }

        /* ---------- Autoplay ---------- */

        initAutoplay() {
            const autoplay = this.params.autoplay;
            this.autoplay = {
                running: false,
                start: () => { this.autoplay.running = true; this.runAutoplay(); },
                stop: () => { this.autoplay.running = false; clearTimeout(this.autoplayTimer); },
            };
            if (!autoplay || this.isLocked) return;
            this.autoplayDelay = autoplay.delay || 3000;
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') clearTimeout(this.autoplayTimer);
                else this.runAutoplay();
            });
            this.autoplay.start();
        }

        runAutoplay() {
            clearTimeout(this.autoplayTimer);
            if (!this.autoplay || !this.autoplay.running || this.isTouched) return;
            this.autoplayTimer = setTimeout(() => {
                if (this.params.loop || !this.isEnd) this.slideNext();
                else this.slideTo(0);
            }, this.autoplayDelay);
        }

        /* ---------- Touch / mouse drag (mirrors Swiper 11 onTouchStart/Move/End) ---------- */

        initTouch() {
            this.onPointerMove = this.onPointerMove.bind(this);
            this.onPointerUp = this.onPointerUp.bind(this);

            this.wrapperEl.addEventListener('pointerdown', (e) => this.onPointerDown(e));
            // Links/images inside slides must not start a native HTML5 drag while swiping
            this.wrapperEl.addEventListener('dragstart', (e) => e.preventDefault());
            // Suppress the click that follows a drag so dragging a card doesn't follow its link (on el, like Swiper)
            this.el.addEventListener('click', (e) => {
                if (this.allowClick) return;
                e.preventDefault();
                e.stopImmediatePropagation();
            }, true);
        }

        onPointerDown(e) {
            if (!this.params.allowTouchMove || this.isLocked || !e.isPrimary) return;
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            // Like Swiper's touchStartPreventDefault: stops text selection while dragging with a mouse.
            // Listeners are on document (no setPointerCapture), so the browser's click synthesis is untouched.
            if (e.pointerType === 'mouse' && !e.target.closest('input, select, option, textarea, button, video, label')) e.preventDefault();

            this.isTouched = true;
            this.isMoved = false;
            this.isScrolling = undefined;
            this.allowClick = true;
            this.startX = e.pageX;
            this.startY = e.pageY;
            this.touchStartTime = Date.now();
            this.startTranslate = this.translate;
            clearTimeout(this.autoplayTimer);

            document.addEventListener('pointermove', this.onPointerMove);
            document.addEventListener('pointerup', this.onPointerUp);
            document.addEventListener('pointercancel', this.onPointerUp);
        }

        onPointerMove(e) {
            if (!this.isTouched || !e.isPrimary) return;
            const diffX = e.pageX - this.startX;
            const diffY = e.pageY - this.startY;

            if (typeof this.isScrolling === 'undefined') {
                if (diffX * diffX + diffY * diffY < THRESHOLD * THRESHOLD) return;
                this.isScrolling = Math.atan2(Math.abs(diffY), Math.abs(diffX)) * 180 / Math.PI > TOUCH_ANGLE;
            }
            if (this.isScrolling) {
                this.onPointerUp(e);
                return;
            }
            if (e.cancelable) e.preventDefault();

            if (!this.isMoved) {
                this.isMoved = true;
                this.allowClick = false;
                if (this.params.loop) {
                    this.loopFix(diffX < 0 ? 'next' : 'prev');
                    this.startTranslate = this.translate;
                }
                this.setTransition(0);
                if (this.params.grabCursor) this.wrapperEl.style.cursor = 'grabbing';
            }
            this.diffX = diffX;
            if (this.params.effect === 'fade') return;

            const min = this.minTranslate();
            const max = this.maxTranslate();
            let current = this.startTranslate + diffX;
            if (diffX > 0 && current > min) {
                current = min - 1 + (-min + this.startTranslate + diffX) ** RESISTANCE_RATIO;
            } else if (diffX < 0 && current < max) {
                current = max + 1 - (max - this.startTranslate - diffX) ** RESISTANCE_RATIO;
            }
            this.setTranslate(current);
        }

        onPointerUp() {
            document.removeEventListener('pointermove', this.onPointerMove);
            document.removeEventListener('pointerup', this.onPointerUp);
            document.removeEventListener('pointercancel', this.onPointerUp);
            if (!this.isTouched) return;
            this.isTouched = false;
            if (this.params.grabCursor && !this.isLocked) this.wrapperEl.style.cursor = 'grab';

            if (!this.isMoved) {
                this.runAutoplay();
                return;
            }
            setTimeout(() => { this.allowClick = true; }, 0);

            const direction = this.diffX < 0 ? 'next' : 'prev';
            const timeDiff = Date.now() - this.touchStartTime;

            if (this.params.effect === 'fade') {
                if (direction === 'next') this.slideNext(); else this.slidePrev();
                return;
            }

            const currentPos = -this.translate;
            if (currentPos < -this.minTranslate()) {
                this.slideTo(this.activeIndex);
                return;
            }
            if (currentPos > -this.maxTranslate()) {
                this.slideTo(this.slides.length < this.snapGrid.length ? this.snapGrid.length - 1 : this.slides.length - 1);
                return;
            }

            const grid = this.slidesGrid;
            let stopIndex = 0;
            let groupSize = this.slidesSizesGrid[0];
            for (let i = 0; i < grid.length; i += 1) {
                if (typeof grid[i + 1] !== 'undefined') {
                    if (currentPos >= grid[i] && currentPos < grid[i + 1]) {
                        stopIndex = i;
                        groupSize = grid[i + 1] - grid[i];
                    }
                } else if (currentPos >= grid[i]) {
                    stopIndex = i;
                    groupSize = grid[grid.length - 1] - grid[grid.length - 2];
                }
            }
            const ratio = (currentPos - grid[stopIndex]) / groupSize;

            if (timeDiff > LONG_SWIPES_MS) {
                if (direction === 'next') this.slideTo(ratio >= LONG_SWIPES_RATIO ? stopIndex + 1 : stopIndex);
                else this.slideTo(ratio > 1 - LONG_SWIPES_RATIO ? stopIndex + 1 : stopIndex);
            } else if (direction === 'next') {
                this.slideTo(stopIndex + 1);
            } else {
                this.slideTo(stopIndex);
            }
        }
    }

    window.Swiper = Swiper;
})();

//# sourceMappingURL=swiper.js.map
